import { pgTable, text, uuid, primaryKey, unique, real, foreignKey, index, } from 'drizzle-orm/pg-core';
import { tableBase, sortOrder } from '../utils';
import { relations, sql } from 'drizzle-orm';
import { AdminBodyPart, AdminEquipment, AdminExerciseMetric, AdminTag, AdminTrainingType, } from './misc';
import { RecordType } from '../enums';
export const AdminLibraryExercise = pgTable('admin_library_exercise', {
    ...tableBase,
    name: text('name').notNull(),
    imageUrl: text('image_url'),
    videoUrl: text('video_url'),
    trainingTypeId: uuid('training_type_id').notNull(),
    // This contains search metadata for full-text search
    // for copying over on initial seeding of an org
    searchVector: text('search_vector'),
    recordType: RecordType('record_type'),
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    typeFk: foreignKey({
        columns: [table.trainingTypeId],
        foreignColumns: [AdminTrainingType.id],
    }),
    searchVectorIdx: index('idx_admin_library_exercise_search').using('gin', sql `${table.searchVector}`),
}));
export const AdminLibraryExercise_Relations = relations(AdminLibraryExercise, ({ many }) => ({
    bodyParts: many(AdminLibraryExerciseBodyPart),
    equipment: many(AdminLibraryExerciseEquipment),
    defaultMetrics: many(AdminLibraryExerciseDefaultMetric),
    tags: many(AdminLibraryExerciseTag),
}));
export const AdminLibraryExerciseBodyPart = pgTable('admin_library_exercise_body_part', {
    ...tableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    bodyPartId: uuid('body_part_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    uq: unique().on(table.libraryExerciseId, table.bodyPartId),
    uq2: unique().on(table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.libraryExerciseId],
        foreignColumns: [AdminLibraryExercise.id],
    }),
    bodyPartFk: foreignKey({
        columns: [table.bodyPartId],
        foreignColumns: [AdminBodyPart.id],
    }),
}));
export const AdminLibraryExerciseBodyPart_Relations = relations(AdminLibraryExerciseBodyPart, ({ one }) => ({
    exercise: one(AdminLibraryExercise, {
        fields: [AdminLibraryExerciseBodyPart.libraryExerciseId],
        references: [AdminLibraryExercise.id],
    }),
    bodyPart: one(AdminBodyPart, {
        fields: [AdminLibraryExerciseBodyPart.bodyPartId],
        references: [AdminBodyPart.id],
    }),
}));
export const AdminLibraryExerciseEquipment = pgTable('admin_library_exercise_equipment', {
    ...tableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    equipmentId: uuid('equipment_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    uq: unique().on(table.libraryExerciseId, table.equipmentId),
    uq2: unique().on(table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.libraryExerciseId],
        foreignColumns: [AdminLibraryExercise.id],
    }),
    equipmentFk: foreignKey({
        columns: [table.equipmentId],
        foreignColumns: [AdminEquipment.id],
    }),
}));
export const AdminLibraryExerciseEquipment_Relations = relations(AdminLibraryExerciseEquipment, ({ one }) => ({
    exercise: one(AdminLibraryExercise, {
        fields: [AdminLibraryExerciseEquipment.libraryExerciseId],
        references: [AdminLibraryExercise.id],
    }),
    equipment: one(AdminEquipment, {
        fields: [AdminLibraryExerciseEquipment.equipmentId],
        references: [AdminEquipment.id],
    }),
}));
export const AdminLibraryExerciseDefaultMetric = pgTable('admin_library_exercise_default_metric', {
    ...tableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    metricId: uuid('metric_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    uq: unique().on(table.libraryExerciseId, table.metricId),
    uq2: unique().on(table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.libraryExerciseId],
        foreignColumns: [AdminLibraryExercise.id],
    }),
    categoryFk: foreignKey({
        columns: [table.metricId],
        foreignColumns: [AdminExerciseMetric.id],
    }),
}));
export const AdminLibraryExerciseDefaultMetric_Relations = relations(AdminLibraryExerciseDefaultMetric, ({ one }) => ({
    exercise: one(AdminLibraryExercise, {
        fields: [AdminLibraryExerciseDefaultMetric.libraryExerciseId],
        references: [AdminLibraryExercise.id],
    }),
    metric: one(AdminExerciseMetric, {
        fields: [AdminLibraryExerciseDefaultMetric.metricId],
        references: [AdminExerciseMetric.id],
    }),
}));
export const AdminLibraryExerciseMaxConversion = pgTable('admin_library_exercise_max_conversion', {
    ...tableBase,
    fromLibraryExerciseId: uuid('from_library_exercise_id').notNull(),
    toLibraryExerciseId: uuid('to_library_exercise_id').notNull(),
    ratio: real('ratio').notNull(),
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    uq: unique().on(table.fromLibraryExerciseId, table.toLibraryExerciseId),
    fromExerciseFk: foreignKey({
        columns: [table.fromLibraryExerciseId],
        foreignColumns: [AdminLibraryExercise.id],
    }),
    targetExerciseFk: foreignKey({
        columns: [table.toLibraryExerciseId],
        foreignColumns: [AdminLibraryExercise.id],
    }),
}));
export const AdminLibraryExerciseMaxConversion_Relations = relations(AdminLibraryExerciseMaxConversion, ({ one }) => ({
    fromLibraryExercise: one(AdminLibraryExercise, {
        fields: [AdminLibraryExerciseMaxConversion.fromLibraryExerciseId],
        references: [AdminLibraryExercise.id],
    }),
    toLibraryExercise: one(AdminLibraryExercise, {
        fields: [AdminLibraryExerciseMaxConversion.toLibraryExerciseId],
        references: [AdminLibraryExercise.id],
    }),
}));
export const AdminLibraryExerciseTag = pgTable('admin_library_exercise_tag', {
    ...tableBase,
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    tagId: uuid('tag_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: primaryKey({
        columns: [table.id],
    }),
    uq: unique().on(table.libraryExerciseId, table.tagId),
    uq2: unique().on(table.libraryExerciseId, table.sortOrder),
    exerciseFk: foreignKey({
        columns: [table.libraryExerciseId],
        foreignColumns: [AdminLibraryExercise.id],
    }),
    tagFk: foreignKey({
        columns: [table.tagId],
        foreignColumns: [AdminTag.id],
    }),
}));
export const AdminLibraryExerciseTag_Relations = relations(AdminLibraryExerciseTag, ({ one }) => ({
    exercise: one(AdminLibraryExercise, {
        fields: [AdminLibraryExerciseTag.libraryExerciseId],
        references: [AdminLibraryExercise.id],
    }),
    tag: one(AdminTag, {
        fields: [AdminLibraryExerciseTag.tagId],
        references: [AdminTag.id],
    }),
}));
//# sourceMappingURL=exercise-library.js.map